/**
 * ENOUGH MARKDOWN TO READ A DESK AID, AND NOT ONE CONSTRUCT MORE.
 *
 * The Resources card mints a signed URL and opens it in a new tab. For a PDF
 * that is exactly right. For a `.md` it is not: the browser hands somebody a
 * download, or a wall of `#` and `|`, and a document written to be read DURING
 * A CUSTOMER CALL is the worst possible thing to make unreadable. The Staff
 * Journey Brief is fourteen tables and a hundred headings; raw, it is useless
 * at the moment it is needed.
 *
 * ---------------------------------------------------------------------------
 * IT PRODUCES DATA, NOT MARKUP, AND THAT IS THE WHOLE SECURITY POSITION.
 *
 * `parseMarkdown` returns an array of plain objects. The component walks them
 * and emits React children, which React escapes. Nothing on this path builds an
 * HTML string, and nothing on it ever should: these documents are uploaded by
 * staff, so the text is INPUT, and a renderer that set HTML from it would make
 * the Resources shelf an injection surface reachable by anyone who can publish.
 *
 * The platform repository has already spent a CI cycle on the weaker version of
 * this rule -- its security suite fails the build on a source file that so much
 * as NAMES React's set-HTML escape hatch, mention or use. Do not reach for it
 * here.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY NOT SUPPORTED
 *
 * No images, no raw HTML passthrough, no reference links, no footnotes, no
 * nested lists. Every one of those is a thing to get wrong for a document class
 * that does not use them. A line this parser does not recognise is rendered as
 * a paragraph -- visible and slightly plain, never swallowed. **Silently
 * dropping a line of an internal procedure is the one failure mode that would
 * matter**, so there is no branch anywhere below that discards input.
 */

/**
 * Inline spans: bold, italic, code.
 *
 * THE ORDERING THAT MATTERS IS IN THE IF-CHAIN BELOW, NOT IN THIS PATTERN, and
 * the first draft of the comment here said the opposite. Swapping the two
 * alternatives in the regex changes nothing, because `[^*]+` excludes asterisks
 * — the italic branch cannot start a match on `**` whatever order it sits in.
 * Swapping the two `startsWith` tests below turns every bold run into an italic
 * wrapping a stray asterisk, which is what the guard actually catches.
 *
 * Recorded because the first break aimed at this line and DID NOT TAKE: twelve
 * tests stayed green against a parser I had deliberately altered, which is the
 * "untested, not proven" case CLAUDE.md names, and the answer was to find the
 * real mechanism rather than to accept the green.
 */
const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

export function parseInline(text) {
  return String(text ?? '')
    .split(INLINE)
    .filter((piece) => piece !== '')
    .map((piece) => {
      if (piece.startsWith('**') && piece.endsWith('**') && piece.length > 4) {
        return { kind: 'strong', text: piece.slice(2, -2) };
      }
      if (piece.startsWith('`') && piece.endsWith('`') && piece.length > 2) {
        return { kind: 'code', text: piece.slice(1, -1) };
      }
      if (piece.startsWith('*') && piece.endsWith('*') && piece.length > 2) {
        return { kind: 'em', text: piece.slice(1, -1) };
      }
      return { kind: 'text', text: piece };
    });
}

const cells = (line) => line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
const isDivider = (line) => /^\|?[\s:-]*-[\s|:-]*\|?$/.test(line) && line.includes('-');

/**
 * Markdown to blocks. Types: h (level), p, ul, ol, table, quote, hr, pre.
 */
export function parseMarkdown(source) {
  const lines = String(source ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  const flushParagraph = (buf) => {
    if (buf.length) blocks.push({ type: 'p', spans: parseInline(buf.join(' ')) });
    return [];
  };

  let para = [];
  while (i < lines.length) {
    const line = lines[i];

    // A fenced block is taken verbatim -- and an UNCLOSED fence runs to the end
    // of the document rather than swallowing the rest as an error, because the
    // text after it is still somebody's procedure.
    if (/^```/.test(line)) {
      para = flushParagraph(para);
      const body = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1;
      blocks.push({ type: 'pre', text: body.join('\n') });
      continue;
    }

    if (!line.trim()) { para = flushParagraph(para); i += 1; continue; }

    if (/^ {0,3}(-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
      para = flushParagraph(para);
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      para = flushParagraph(para);
      blocks.push({
        type: 'h', level: heading[1].length, spans: parseInline(heading[2].trim()),
      });
      i += 1;
      continue;
    }

    // A table needs a header row and a divider under it. Without the divider it
    // is not a table, and the lines fall through to paragraphs rather than
    // being guessed at.
    if (line.includes('|') && isDivider(lines[i + 1] || '')) {
      para = flushParagraph(para);
      const head = cells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(cells(lines[i]));
        i += 1;
      }
      blocks.push({
        type: 'table',
        head: head.map(parseInline),
        rows: rows.map((r) => r.map(parseInline)),
      });
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      para = flushParagraph(para);
      const body = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', spans: parseInline(body.join(' ')) });
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      para = flushParagraph(para);
      const ordered = Boolean(numbered);
      const items = [];
      while (i < lines.length) {
        const m = ordered
          ? lines[i].match(/^\s*\d+[.)]\s+(.*)$/)
          : lines[i].match(/^\s*[-*+]\s+(.*)$/);
        if (!m) break;
        const parts = [m[1]];
        i += 1;
        // A wrapped list item: an indented continuation belongs to the item
        // above it, not to a new paragraph.
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*+]|\d+[.)])\s/.test(lines[i])) {
          parts.push(lines[i].trim());
          i += 1;
        }
        items.push(parseInline(parts.join(' ')));
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      continue;
    }

    para.push(line.trim());
    i += 1;
  }
  flushParagraph(para);
  return blocks;
}

/** Whether a document should be read in the Portal rather than downloaded. */
export function isMarkdown(doc) {
  const path = String(doc?.storage_path || '');
  const type = String(doc?.content_type || '');
  return /\.mde?$/i.test(path) || /\.markdown$/i.test(path)
    || type === 'text/markdown' || type === 'text/x-markdown';
}
